import React from "react";
import moment from "moment-timezone";
import { v1 as uuidv1 } from "uuid";

import { fetchApi, withAccessToken } from "../../Network/NetworkService";
import { endpoint } from "../../Constants";
import { notif } from "../Ui/Notification";

import "../../Assets/Pages/dermatoSdr.css";
import IconTooltip from "../Ui/IconTooltip";

// Keep in sync with MAX_RANGE_DAYS in WhatsappOptinController.js.
const MAX_RANGE_DAYS = 31;

// Same rule as isValidTestEmail in WhatsappOptinController.js.
const isValidTestEmail = (s) => {
  const v = (s || "").trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) && !/[^\x20-\x7E]/.test(v);
};

// Campagne « opt-in WhatsApp » : email aux patients ayant consulté (tous
// types de consultation), les invitant à consentir aux
// notifications WhatsApp. Le CTA de l'email est un lien auto-login vers la
// page de consentement du site patient (/notifications-whatsapp) — le
// consentement lui-même est écrit par feeli-server quand le patient valide.
class WhatsappOptin extends React.Component {
  constructor() {
    super();
    const today = moment().tz("Europe/Paris").format("YYYY-MM-DD");
    this.state = {
      startDate: today,
      endDate: today,
      testMode: false,
      testRealUser: false,
      testEmail: "",
      // Entonnoir de diagnostic (route /funnel) : null = replié.
      funnel: null,
      funnelConsented: [],
      funnelLoading: false,
      users: [],
      loading: false,
      sending: false,
      sendProgress: "0",
      usersLoaded: false,
      sendEvent: null,
      page: 1,
      totalPage: 0,
      countUsers: 0,
    };
  }

  componentDidMount() {
    this.fetchUsers();
  }

  componentWillUnmount() {
    if (this.state.sendEvent) {
      this.state.sendEvent.close();
    }
  }

  rangeDays = () =>
    moment(this.state.endDate).diff(moment(this.state.startDate), "days") + 1;

  rangeTooWide = () => this.rangeDays() > MAX_RANGE_DAYS;

  // Entonnoir de diagnostic : où les patients de la période sont éliminés,
  // garde par garde (calculé côté serveur, route /funnel).
  toggleFunnel = () => {
    if (this.state.funnel) {
      this.setState({ funnel: null });
      return;
    }
    if (this.rangeTooWide()) return;
    this.setState({ funnelLoading: true });
    fetchApi(
      "/whatsapp-optin/funnel?start=" +
        this.state.startDate +
        "&end=" +
        this.state.endDate,
    )
      .then((res) => {
        this.setState({
          funnel: res.steps || [],
          funnelConsented: res.consented || [],
          funnelLoading: false,
        });
      })
      .catch(() => {
        notif.addNotification(
          "Erreur",
          "Erreur de récupération du diagnostic",
          "warning",
        );
        this.setState({ funnelLoading: false });
      });
  };

  fetchUsers = () => {
    if (this.rangeTooWide()) {
      this.setState({ users: [], usersLoaded: true, loading: false });
      return;
    }
    // funnel remis à zéro : il correspond à la période affichée.
    this.setState({ loading: true, funnel: null });
    const url =
      "/whatsapp-optin/list?start=" +
      this.state.startDate +
      "&end=" +
      this.state.endDate +
      "&page=" +
      this.state.page;
    fetchApi(url)
      .then((res) => {
        this.setState({
          users: res.users || [],
          totalPage: res.countPages || 0,
          countUsers: res.countUsers || 0,
          usersLoaded: true,
          loading: false,
        });
      })
      .catch(() => {
        notif.addNotification(
          "Erreur",
          "Erreur de récupération des patients",
          "warning",
        );
        this.setState({ loading: false });
      });
  };

  handleStartChange = (e) => {
    const startDate = e.target.value;
    const endDate =
      moment(this.state.endDate).isBefore(moment(startDate)) ||
      !this.state.endDate
        ? startDate
        : this.state.endDate;
    this.setState(
      { startDate, endDate, users: [], usersLoaded: false, page: 1 },
      this.fetchUsers,
    );
  };

  handleEndChange = (e) => {
    this.setState(
      { endDate: e.target.value, users: [], usersLoaded: false, page: 1 },
      this.fetchUsers,
    );
  };

  updatePage(page) {
    this.setState({ users: [], page, loading: true }, () => {
      this.fetchUsers();
    });
  }

  sendToAll = () => {
    if (
      this.state.sending ||
      this.rangeTooWide() ||
      (!this.state.testMode && !this.state.users.length)
    )
      return;

    const { testMode, testRealUser, testEmail, countUsers } = this.state;
    const trimmedTest = (testEmail || "").trim();

    if (testMode && !isValidTestEmail(trimmedTest)) {
      notif.addNotification(
        "Erreur",
        "Renseignez une adresse email de test valide (caractères ASCII).",
        "warning",
      );
      return;
    }

    const confirmMsg = testMode
      ? testRealUser
        ? "Mode TEST RÉEL : envoyer 1 email à " +
          trimmedTest +
          " avec un vrai lien auto-login pour CE compte ? Aucun autre utilisateur contacté."
        : "Mode TEST : envoyer 1 email d'exemple à " +
          trimmedTest +
          " ? Aucun utilisateur réel contacté."
      : "Envoyer l'invitation WhatsApp (opt-in) à " +
        countUsers +
        " patient(s) ? De vrais emails seront envoyés.";
    if (!window.confirm(confirmMsg)) return;

    const tokenId = uuidv1();
    const sendEvent = new EventSource(
      withAccessToken(endpoint + "/whatsapp-optin/sendProgress?token=" + tokenId),
    );
    this.setState({ sending: true, sendProgress: "0", sendEvent });

    let posted = false;
    let settled = false;
    let fallbackTimer = null;
    let watchdog = null;

    const reset = () => {
      if (settled) return;
      settled = true;
      clearTimeout(fallbackTimer);
      clearTimeout(watchdog);
      this.setState({ sending: false, sendProgress: "0", sendEvent: null });
      sendEvent.close();
    };

    const refreshAfterSend = () => {
      this.setState({ page: 1 }, this.fetchUsers);
    };

    // Watchdog anti-spinner : si le flux SSE se tait, l'envoi continue côté
    // serveur et l'historique fait foi — voir GdprWarning.jsx.
    const bumpWatchdog = () => {
      clearTimeout(watchdog);
      watchdog = setTimeout(() => {
        if (settled) return;
        notif.addNotification(
          "Info",
          "Envoi lancé. Consultez l'historique pour le résultat.",
          "info",
        );
        reset();
        refreshAfterSend();
      }, 30000);
    };

    const triggerSend = () => {
      if (posted) return;
      posted = true;
      clearTimeout(fallbackTimer);
      bumpWatchdog();
      fetchApi("/whatsapp-optin/send", {
        method: "POST",
        body: JSON.stringify({
          start: this.state.startDate,
          end: this.state.endDate,
          id: tokenId,
          testMode: testMode,
          testRealUser: testMode ? testRealUser : false,
          testEmail: testMode ? trimmedTest : null,
        }),
      }).catch(() => {
        notif.addNotification("Erreur", "Erreur d'envoi", "warning");
        reset();
      });
    };

    fallbackTimer = setTimeout(triggerSend, 5000);
    sendEvent.onopen = triggerSend;

    sendEvent.onmessage = (ev) => {
      if (posted) bumpWatchdog();
      if (ev.data === "start") {
        triggerSend();
        return;
      }
      if (ev.data.startsWith("fail&")) {
        const parts = ev.data.split("&");
        const total = parseInt(parts[1]) || 0;
        const failCount = parseInt(parts[2]) || 0;
        notif.addNotification(
          "Error",
          total + " envoyés, " + failCount + " échecs",
          "warning",
        );
        reset();
        refreshAfterSend();
      } else if (ev.data.includes("error")) {
        notif.addNotification(
          "Erreur",
          ev.data.split("&")[1] || "Erreur d'envoi",
          "warning",
        );
        reset();
      } else if (ev.data.startsWith("done&")) {
        const parts = ev.data.split("&");
        const total = parseInt(parts[1]) || 0;
        notif.addNotification(
          "Succès",
          testMode
            ? "Email de test envoyé à " + trimmedTest
            : total + " invitations WhatsApp envoyées",
          "success",
        );
        reset();
        refreshAfterSend();
      } else if (ev.data === "empty") {
        notif.addNotification("Info", "Aucun patient à traiter", "info");
        reset();
      } else {
        this.setState({ sendProgress: ev.data });
      }
    };

    sendEvent.onerror = () => {
      // Pré-POST : le fallback déclenche l'envoi. Post-POST : le watchdog
      // libère le spinner, l'envoi continue côté serveur.
    };
  };

  render() {
    const {
      startDate,
      endDate,
      users,
      loading,
      sending,
      sendProgress,
      usersLoaded,
      page,
      totalPage,
      countUsers,
      testMode,
      testRealUser,
      testEmail,
    } = this.state;

    const maxDate = moment().tz("Europe/Paris").format("YYYY-MM-DD");
    const tooWide = this.rangeTooWide();

    return (
      <div className="dermato-sdr-container">
        <div className="dermato-sdr-header">
          <h2 className="dermato-sdr-title">
            {"Opt-in WhatsApp — Invitation par email"}
          </h2>
          <div>
            <button
              className="dermato-sdr-btn dermatosdr-btn-link"
              onClick={() =>
                this.props.history.push("/whatsapp-optin/activations")
              }
            >
              {"Activations"}
            </button>
            <button
              className="dermato-sdr-btn dermatosdr-btn-link"
              onClick={() =>
                this.props.history.push("/whatsapp-optin/historique")
              }
            >
              {"Historique des envois"}
            </button>
          </div>
        </div>

        <div className="dermato-sdr-card">
          <h3 className="dermato-sdr-card-title">Filtres</h3>
          <p style={{ color: "#888", fontSize: 14, marginTop: -8 }}>
            {
              "Patients ayant une consultation aboutie (tous types) dans la période choisie, sans consentement WhatsApp et jamais invités. L'email propose d'activer les notifications WhatsApp de suivi (lien auto-login vers la page de consentement)."
            }
          </p>
          <div className="dermato-sdr-inline">
            <div className="dermato-sdr-form-group">
              <label>Du</label>
              <input
                type="date"
                className="dermato-sdr-input"
                value={startDate}
                max={maxDate}
                onChange={this.handleStartChange}
              />
            </div>
            <div className="dermato-sdr-form-group">
              <label>Au</label>
              <input
                type="date"
                className="dermato-sdr-input"
                value={endDate}
                min={startDate}
                max={maxDate}
                onChange={this.handleEndChange}
              />
            </div>
            <div style={{ alignSelf: "flex-end" }}>
              <button
                className="dermato-sdr-btn dermatosdr-btn-primary"
                onClick={this.sendToAll}
                disabled={sending || tooWide || (!testMode && !users.length)}
              >
                {sending
                  ? "Envoi... " + sendProgress + "%"
                  : testMode
                    ? "Envoyer un test"
                    : "Envoyer l'invitation WhatsApp"}
              </button>
            </div>
          </div>

          <div className="dermato-sdr-testmode">
            <label className="dermato-sdr-checkbox">
              <input
                type="checkbox"
                checked={testMode}
                disabled={sending}
                onChange={(e) => this.setState({ testMode: e.target.checked })}
              />
              <span>{"Mode test"}</span>
            </label>
            {testMode && (
              <React.Fragment>
                <input
                  type="email"
                  className="dermato-sdr-input"
                  style={{ maxWidth: 260 }}
                  placeholder="email de test"
                  value={testEmail}
                  disabled={sending}
                  onChange={(e) => this.setState({ testEmail: e.target.value })}
                />
                <label className="dermato-sdr-checkbox">
                  <input
                    type="checkbox"
                    checked={testRealUser}
                    disabled={sending}
                    onChange={(e) =>
                      this.setState({ testRealUser: e.target.checked })
                    }
                  />
                  <span>{"Test réel (mon compte)"}</span>
                </label>
                <span style={{ color: "#888", fontSize: 13 }}>
                  {testRealUser
                    ? "1 email envoyé à cette adresse avec un VRAI lien auto-login pour le compte Feeli correspondant. Aucun flag posé."
                    : "1 email d'exemple (données fictives, lien factice) envoyé à cette adresse — aucun utilisateur réel contacté."}
                </span>
              </React.Fragment>
            )}
          </div>
          {tooWide && (
            <p style={{ color: "#c62828", fontSize: 14, marginTop: 8 }}>
              {"Période trop large : maximum " + MAX_RANGE_DAYS + " jours."}
            </p>
          )}
        </div>

        <div className="dermato-sdr-card" style={{ marginTop: 24 }}>
          <h3 className="dermato-sdr-card-title">
            Patients ({countUsers})
            <button
              className="dermato-sdr-btn dermatosdr-btn-link"
              style={{ marginLeft: 12, fontSize: 13 }}
              disabled={this.state.funnelLoading}
              onClick={this.toggleFunnel}
            >
              {this.state.funnelLoading
                ? "Analyse..."
                : this.state.funnel
                  ? "Masquer le diagnostic"
                  : "Pourquoi ce nombre ?"}
            </button>
          </h3>

          {this.state.funnel && (
            <div
              style={{
                background: "#f8f9fa",
                borderRadius: 8,
                padding: "10px 14px",
                marginBottom: 14,
                fontSize: 14,
                color: "#14375F",
              }}
            >
              {this.state.funnel.map((s, i) => {
                const prev = i > 0 ? this.state.funnel[i - 1].count : null;
                const lost = prev !== null ? prev - s.count : 0;
                return (
                  <div key={s.label} style={{ padding: "2px 0" }}>
                    <strong>{s.count}</strong> — {s.label}
                    {lost > 0 && (
                      <span style={{ color: "#c62828", marginLeft: 8 }}>
                        (−{lost})
                      </span>
                    )}
                  </div>
                );
              })}
              <p style={{ color: "#888", fontSize: 13, margin: "6px 0 0" }}>
                {
                  "Chaque ligne ajoute une condition à la précédente ; (−N) = patients éliminés par cette condition."
                }
              </p>
              {(this.state.funnelConsented || []).length > 0 && (
                <div style={{ marginTop: 10, borderTop: "1px solid #e0e0e0", paddingTop: 8 }}>
                  <p style={{ fontWeight: 600, margin: "0 0 4px" }}>
                    {"Détail des « déjà consentants » (source · jour d'activation) :"}
                  </p>
                  {this.state.funnelConsented.map((c) => (
                    <div key={c.source + c.day} style={{ padding: "1px 0" }}>
                      <strong>{c.count}</strong> — {c.source} · {c.day}
                    </div>
                  ))}
                  <p style={{ color: "#888", fontSize: 13, margin: "6px 0 0" }}>
                    {
                      "Des dates étalées avec une source (profile, email-optin) = consentements réels. Un gros bloc sur un même jour sans source = flag posé en masse, à traiter comme un non-consentement."
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {loading ? (
            <div className="dermato-sdr-empty">
              <span className="loader" />
            </div>
          ) : usersLoaded && users.length === 0 ? (
            <div className="dermato-sdr-empty">
              {"Aucun patient éligible trouvé pour cette période."}
            </div>
          ) : users.length > 0 ? (
            <div>
              <table className="dermato-sdr-table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Email</th>
                    <th>{"Téléphone"}</th>
                    <th>{"Dernière consultation"}</th>
                    <th>{"Consultations"}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u._id}>
                      <td>
                        {u.FName} {u.LName}
                      </td>
                      <td>{u.email}</td>
                      <td>{u.phone}</td>
                      <td>
                        {moment(u.latestRequestDate).format("DD/MM/YYYY HH:mm")}
                      </td>
                      <td>{u.requestCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="dermatosdr-pagination">
                <p style={{ margin: 0, color: "#888", fontSize: 14 }}>
                  Page <strong>{page}</strong> / {totalPage}
                </p>
                <div>
                  {page > 1 && (
                    <IconTooltip
                      icon="fa fa-chevron-left"
                      text="Page précédente"
                      onClick={() => this.updatePage(page - 1)}
                    />
                  )}
                  {page < totalPage && (
                    <IconTooltip
                      icon="fa fa-chevron-right"
                      text="Page suivante"
                      onClick={() => this.updatePage(page + 1)}
                    />
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }
}

export default WhatsappOptin;
